import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Upload, FileText, FileCode2, FileType2, Star, StarOff, Trash2, Eye, Download, Copy,
  ClipboardList, ScrollText,
} from "lucide-react";

const TYPES = [
  { v: "job_card", l: "Job Cards", icon: ClipboardList, hint: "Printed work-order handed to inspectors on site." },
  { v: "certificate", l: "Certificates", icon: ScrollText, hint: "Compliance certificates issued after inspection sign-off." },
];

const FORMAT_META = {
  html: { label: "HTML", icon: FileCode2, tint: "bg-[#0284C7]" },
  docx: { label: "DOCX", icon: FileText, tint: "bg-[#01ADEF]" },
  pdf:  { label: "PDF",  icon: FileType2, tint: "bg-[#DC2626]" },
};

export default function Templates() {
  const { user } = useAuth();
  const [type, setType] = useState("certificate");
  const [items, setItems] = useState([]);
  const [variables, setVariables] = useState({});
  const [uploadOpen, setUploadOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, v] = await Promise.all([
        api.get("/templates", { params: { type } }),
        api.get("/templates/variables"),
      ]);
      setItems(t.data); setVariables(v.data);
    } catch (err) {
      console.debug("templates load failed", err?.message);
      toast.error("Failed to load templates");
    } finally { setLoading(false); }
  }, [type]);
  useEffect(() => { load(); }, [load]);

  if (user && !["admin","super_admin"].includes(user.role)) return <Navigate to="/dashboard" replace />;

  const setDefault = async (id) => {
    try { await api.post(`/templates/${id}/set-default`); toast.success("Default template updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id, name) => {
    if (!confirm(`Delete template "${name}"?`)) return;
    try { await api.delete(`/templates/${id}`); toast.success("Template deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const download = async (id, filename) => {
    try {
      const res = await api.get(`/templates/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = filename || "template"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download"); }
  };
  const preview = async (id) => {
    try {
      const res = await api.post(`/templates/preview?type=${type}&template_id=${id}`, {}, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
      // Give browser time to open before revoking
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      const msg = e.response?.data instanceof Blob ? await e.response.data.text() : "Preview failed";
      try { toast.error(JSON.parse(msg).detail || "Preview failed"); } catch { toast.error(msg); }
    }
  };

  const currentVars = variables[type] || [];
  const defaults = items.filter(t => t.is_default);
  const hasDefault = defaults.length > 0;

  return (
    <div className="p-6 md:p-8" data-testid="templates-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Configuration</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Document Templates</h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Upload branded HTML, DOCX or PDF templates and mark one as <b>default</b> per document type. The default
            template is used automatically when generating job cards and certificates.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
            <DialogTrigger asChild>
              <Button data-testid="upload-template-btn" className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">
                <Upload className="w-4 h-4 mr-2" /> Upload Template
              </Button>
            </DialogTrigger>
            <UploadDialog type={type} onDone={() => { setUploadOpen(false); load(); }} />
          </Dialog>
        </div>
      </div>

      {/* Type switcher */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        {TYPES.map(t => {
          const active = type === t.v;
          const Icon = t.icon;
          return (
            <button
              key={t.v}
              data-testid={`tpl-type-${t.v}`}
              onClick={() => setType(t.v)}
              className={`bg-white border p-4 text-left transition-all ${active ? "border-[#01ADEF] shadow-[0_0_0_1px_#01ADEF]" : "border-gray-200 hover:border-gray-300"}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wider font-bold text-gray-500">{t.l}</div>
                <div className={`w-8 h-8 ${active ? "bg-[#01ADEF]" : "bg-gray-900"} flex items-center justify-center`}>
                  <Icon className="w-4 h-4 text-white" strokeWidth={2.5} />
                </div>
              </div>
              <div className="text-xs text-gray-500 leading-relaxed">{t.hint}</div>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Templates list */}
        <div className="lg:col-span-2 bg-white border border-gray-200">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="font-display text-lg font-bold tracking-tight">
              {TYPES.find(t => t.v === type)?.l}
              <span className="ml-2 font-mono text-xs text-gray-500">({items.length})</span>
            </h2>
            {!hasDefault && items.length > 0 && (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-[#FEF3C7] text-[#92400E] border border-[#FCD34D] px-2 py-1">
                No default set
              </span>
            )}
          </div>
          <div className="divide-y divide-gray-100" data-testid="tpl-list">
            {loading ? (
              <div className="p-8 text-center text-gray-500 text-sm">Loading…</div>
            ) : items.length === 0 ? (
              <EmptyState type={type} onUpload={() => setUploadOpen(true)} />
            ) : items.map(t => {
              const meta = FORMAT_META[t.format] || FORMAT_META.pdf;
              const FmtIcon = meta.icon;
              return (
                <div key={t.id} className="px-5 py-4 flex items-center gap-4 hover:bg-gray-50" data-testid={`tpl-row-${t.id}`}>
                  <div className={`w-10 h-10 ${meta.tint} flex items-center justify-center shrink-0`}>
                    <FmtIcon className="w-5 h-5 text-white" strokeWidth={2.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-semibold truncate">{t.name}</div>
                      {t.is_default && (
                        <span data-testid={`tpl-default-badge-${t.id}`} className="text-[10px] font-bold uppercase tracking-wider bg-[#D1FAE5] text-[#065F46] border border-[#6EE7B7] px-2 py-0.5 inline-flex items-center gap-1">
                          <Star className="w-3 h-3" strokeWidth={2.5} /> Default
                        </span>
                      )}
                      <span className="text-[10px] font-mono text-gray-500 uppercase">{meta.label}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 truncate">
                      {t.original_filename} · {(t.size / 1024).toFixed(1)} KB · by {t.uploaded_by_name || "admin"} · {new Date(t.uploaded_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <IconBtn testid={`tpl-preview-${t.id}`} onClick={() => preview(t.id)} title="Preview with sample data" icon={Eye} />
                    <IconBtn testid={`tpl-download-${t.id}`} onClick={() => download(t.id, t.original_filename)} title="Download original" icon={Download} />
                    {t.is_default ? (
                      <IconBtn onClick={() => toast("Already the default")} title="Default" icon={Star} className="text-[#059669]" />
                    ) : (
                      <IconBtn testid={`tpl-set-default-${t.id}`} onClick={() => setDefault(t.id)} title="Set as default" icon={StarOff} />
                    )}
                    <IconBtn testid={`tpl-delete-${t.id}`} onClick={() => remove(t.id, t.name)} title="Delete" icon={Trash2} className="text-gray-400 hover:text-[#DC2626]" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Variables panel */}
        <div className="bg-white border border-gray-200 self-start">
          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="font-display text-lg font-bold tracking-tight">Available Variables</h2>
            <p className="text-xs text-gray-500 mt-1">Use these placeholders inside your template. Syntax: <code className="font-mono bg-gray-100 px-1 py-0.5 text-[11px]">{"{{variable_name}}"}</code></p>
          </div>
          <div className="max-h-[520px] overflow-y-auto divide-y divide-gray-100" data-testid="tpl-variables-list">
            {currentVars.map(v => (
              <VariableRow key={v.key} v={v} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function IconBtn({ onClick, title, icon: Icon, className = "", testid }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`w-8 h-8 flex items-center justify-center border border-transparent hover:bg-gray-100 hover:border-gray-200 transition-colors text-gray-500 ${className}`}
    >
      <Icon className="w-4 h-4" strokeWidth={2} />
    </button>
  );
}

function VariableRow({ v }) {
  const snippet = `{{${v.key}}}`;
  return (
    <div className="px-4 py-2.5 hover:bg-gray-50 flex items-center justify-between gap-2 group">
      <div className="min-w-0">
        <div className="font-mono text-xs font-bold text-[#01ADEF]">{snippet}</div>
        <div className="text-[11px] text-gray-500 truncate">{v.label} · <span className="italic">e.g. {v.example}</span></div>
      </div>
      <button
        onClick={() => { navigator.clipboard.writeText(snippet); toast.success("Copied"); }}
        className="opacity-0 group-hover:opacity-100 transition-opacity w-7 h-7 flex items-center justify-center border border-gray-200 hover:bg-white text-gray-500"
        title="Copy"
      >
        <Copy className="w-3 h-3" />
      </button>
    </div>
  );
}

function EmptyState({ type, onUpload }) {
  const meta = TYPES.find(t => t.v === type);
  return (
    <div className="p-10 text-center">
      <div className="w-14 h-14 bg-gray-100 mx-auto flex items-center justify-center mb-3">
        <Upload className="w-6 h-6 text-gray-400" strokeWidth={2} />
      </div>
      <div className="font-display text-lg font-bold tracking-tight">No {meta?.l.toLowerCase()} template yet</div>
      <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
        Upload an HTML, DOCX or PDF template. Insert placeholders like <code className="font-mono text-[11px] bg-gray-100 px-1 py-0.5">{"{{customer_name}}"}</code> to have them auto-filled at generation time.
      </p>
      <Button onClick={onUpload} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6] mt-4">
        <Upload className="w-4 h-4 mr-2" /> Upload {meta?.l.slice(0, -1)}
      </Button>
    </div>
  );
}

function UploadDialog({ type, onDone }) {
  const [name, setName] = useState("");
  const [makeDefault, setMakeDefault] = useState(true);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const submit = async () => {
    if (!name.trim()) return toast.error("Give the template a name");
    if (!file) return toast.error("Choose a template file (HTML, DOCX, or PDF)");
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post(`/templates?type=${type}&name=${encodeURIComponent(name)}&make_default=${makeDefault}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Template uploaded");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const acceptStr = ".html,.htm,.docx,.pdf";
  const label = TYPES.find(t => t.v === type)?.l.slice(0, -1) || "template";

  return (
    <DialogContent className="rounded-md max-w-lg">
      <DialogHeader>
        <DialogTitle className="font-display">Upload {label} Template</DialogTitle>
        <DialogDescription className="text-xs text-gray-500">
          Accepted formats: HTML, DOCX, PDF. Use <code className="font-mono bg-gray-100 px-1">{"{{variable}}"}</code> placeholders for dynamic fields.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-2">
        <div>
          <Label className="text-xs uppercase tracking-wider font-semibold">Template Name *</Label>
          <Input data-testid="tpl-name-input" value={name} onChange={e => setName(e.target.value)} placeholder={`e.g. ${label} — Blue Header v2`} className="rounded-md mt-1" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider font-semibold">File *</Label>
          <div className="mt-1 border border-dashed border-gray-300 hover:border-[#01ADEF] transition-colors">
            <input ref={fileRef} type="file" accept={acceptStr} onChange={e => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="tpl-file-input" />
            <button type="button" onClick={() => fileRef.current?.click()} className="w-full p-6 text-center hover:bg-gray-50">
              {file ? (
                <div>
                  <div className="font-mono text-sm font-semibold">{file.name}</div>
                  <div className="text-[11px] text-gray-500 mt-1">{(file.size / 1024).toFixed(1)} KB · {file.type || "unknown"}</div>
                </div>
              ) : (
                <div>
                  <Upload className="w-6 h-6 mx-auto text-gray-400" strokeWidth={2} />
                  <div className="text-sm text-gray-600 mt-2">Click to select an <b>.html</b>, <b>.docx</b> or <b>.pdf</b> file</div>
                  <div className="text-[11px] text-gray-500 mt-0.5">Max 8MB</div>
                </div>
              )}
            </button>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input type="checkbox" data-testid="tpl-make-default" checked={makeDefault} onChange={e => setMakeDefault(e.target.checked)} className="w-4 h-4" />
          Set as default for this document type
        </label>
      </div>
      <DialogFooter>
        <Button variant="outline" className="rounded-full" onClick={onDone}>Cancel</Button>
        <Button data-testid="tpl-upload-submit" disabled={busy} onClick={submit} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">
          {busy ? "Uploading…" : "Upload"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
const newLineItem = () => ({ _key: uid(), description: "", qty: 1, unit_price: 0 });

